import type { WidthRow } from "@components/WidthHistoryEditor";

export type WidthSavePlan = {
  /** Send `width` on the device PATCH: the user changed the width field. */
  sendWidth: boolean;
  /** The whole timeline to PUT, or null when it is unchanged. */
  rows: WidthRow[] | null;
};

/**
 * What saving the device dialog writes for header width.
 *
 * `baselineMeters` is the width the dialog opened with: the newest loaded
 * timeline segment, or the device's own width when it has none (null when
 * creating). Only a field that differs from it is a width edit. The dialog used
 * to send the field on every save, so renaming a John Deere combine before its
 * first import stamped the 6.7 m placeholder as a hand-entered width, and once
 * an import had written Deere's width it overwrote that row with the stale
 * value the field was prefilled with.
 *
 * The field is the CURRENT width, so it is written into the newest row rather
 * than opening a new one; a dated change is already on the end of `rows`. That
 * happens whenever the two differ, not only when the field differs from the
 * baseline: after the history editor moved the newest row, retyping the
 * original width must put it back. An untouched field cannot differ — `rows`
 * is null until the timeline loads, and then the field follows its newest row
 * unless the user typed.
 */
export const planWidthSave = ({
  meters,
  baselineMeters,
  rows,
  loadedRows,
}: {
  meters: number;
  baselineMeters: number | null;
  rows: WidthRow[] | null;
  loadedRows: WidthRow[];
}): WidthSavePlan => {
  const sendWidth = baselineMeters == null || meters !== baselineMeters;
  let next = rows;
  if (next && next.length > 0) {
    const last = next[next.length - 1];
    if (last.widthMeters !== meters) {
      next = [...next.slice(0, -1), { ...last, widthMeters: meters }];
    }
  }
  if (!next || next.length === 0) return { sendWidth, rows: null };
  const changed =
    next.length !== loadedRows.length ||
    next.some(
      (r, i) =>
        loadedRows[i] == null ||
        r.widthMeters !== loadedRows[i].widthMeters ||
        // The earliest row governs all earlier time; its own instant is immaterial.
        (i > 0 && r.effectiveFrom !== loadedRows[i].effectiveFrom)
    );
  return { sendWidth, rows: changed ? next : null };
};
